import axios from "axios";

/*
 * ---------------------------------------------------------------------------
 * 1471 Horwich Squadron API
 * ---------------------------------------------------------------------------
 *
 * Authentication is now device based.
 *
 * The access token stored here may represent:
 *
 *   staff              - one of the four staff PCs
 *   attendance_kiosk   - the sign in/out tablet
 *   cadet_training     - one of the twenty cadet tablets
 *   display            - the main display
 *
 * The permanent device code/secret are handled by AuthContext.
 * This file only handles the temporary device session token.
 */

// ---------------------------------------------------------------------------
// Backend URL
// ---------------------------------------------------------------------------

const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || ""
).replace(/\/+$/, "");

export const API_BASE = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Session token
// ---------------------------------------------------------------------------
//
// Kept under the existing key so the rest of the site does not break.
//
// If an old personal-login token is still present, AuthContext will try
// /device/me, reject the old token and replace it with a device session.
//
export const TOKEN_KEY = "horwich_token";

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    Accept: "application/json",
  },
});

// ---------------------------------------------------------------------------
// Automatically attach the current device/session token
// ---------------------------------------------------------------------------

api.interceptors.request.use(
  (config) => {
    const token = getToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

export const setToken = (token) => {
  if (!token) {
    clearToken();
    return;
  }

  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error("Unable to store squadron session token:", error);
  }
};

export const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error("Unable to clear squadron session token:", error);
  }
};

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Device roles
// ---------------------------------------------------------------------------
//
// These are the actual roles used by the new backend device register.
//

export const DEVICE_ROLES = {
  STAFF: "staff",
  ATTENDANCE: "attendance_kiosk",
  CADET: "cadet_training",
  DISPLAY: "display",
};

export const DEVICE_ROLE_LABELS = {
  staff: "Staff Computer",
  attendance_kiosk: "Sign In / Out Tablet",
  cadet_training: "Cadet Training Tablet",
  display: "Squadron Display",
};

// ---------------------------------------------------------------------------
// Legacy user role labels
// ---------------------------------------------------------------------------
//
// Do not remove these yet.
//
// A registered staff computer is deliberately represented by server.py as an
// anonymous admin-style user so the existing staff Dashboard can continue to
// work while the rest of the site is converted to device authentication.
//

export const ROLE_LABELS = {
  admin: "Squadron Admin",
  cfav: "Adult Volunteer (CFAV)",
  cadet: "Cadet",
  parent: "Parent / Carer",
};

// ---------------------------------------------------------------------------
// Useful helpers
// ---------------------------------------------------------------------------

export const isStaffDevice = (device) =>
  device?.role === DEVICE_ROLES.STAFF;

export const isAttendanceDevice = (device) =>
  device?.role === DEVICE_ROLES.ATTENDANCE;

export const isCadetDevice = (device) =>
  device?.role === DEVICE_ROLES.CADET;

export const isDisplayDevice = (device) =>
  device?.role === DEVICE_ROLES.DISPLAY;

export const deviceHomePath = (device) => {
  switch (device?.role) {
    case DEVICE_ROLES.STAFF:
      return "/staff";

    case DEVICE_ROLES.ATTENDANCE:
      return "/attendance";

    case DEVICE_ROLES.CADET:
      return "/training";

    case DEVICE_ROLES.DISPLAY:
      return "/display";

    default:
      return "/portal";
  }
};

export default api;